import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const GalleryPage = () => {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  
  const [photos, setPhotos] = useState([]);
  const [myPhotos, setMyPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('gallery'); // 'gallery' or 'collection'
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [downloading, setDownloading] = useState(null); // photo_id being downloaded
  const [downloadProgress, setDownloadProgress] = useState(0);

  const fetchPhotos = useCallback(async () => {
    try {
      setLoading(true);
      const [galleryRes, myPhotosRes] = await Promise.all([
        axios.get(`${API}/gallery/photos`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/gallery/my-photos`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setPhotos(galleryRes.data.data || []);
      setMyPhotos(myPhotosRes.data.data || []);
    } catch (error) {
      console.error('Error fetching photos:', error);
      toast.error('Gagal memuat galeri');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchPhotos();
    }
  }, [token, fetchPhotos]);

  const handlePurchase = async (photo) => {
    if (purchasing) return;
    
    const userBalance = user?.tokens_balance || 0;
    if (userBalance < photo.harga) {
      toast.error(`Saldo token tidak cukup. Dibutuhkan ${photo.harga} token.`);
      return;
    }
    
    if (!window.confirm(`Beli foto ini dengan ${photo.harga} token?`)) {
      return;
    }
    
    setPurchasing(true);
    try {
      const response = await axios.post(`${API}/gallery/purchase`, 
        { photo_id: photo.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Pembelian berhasil! Foto ditambahkan ke koleksi Anda.');
      
      // Refresh data
      fetchPhotos();
      if (refreshUser) refreshUser();
      
      // Show download option
      setSelectedPhoto({
        ...photo,
        is_purchased: true
      });
      
    } catch (error) {
      console.error('Error purchasing photo:', error);
      toast.error(error.response?.data?.detail || 'Gagal membeli foto');
    } finally {
      setPurchasing(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleDownload = async (photoId, filename) => {
    if (downloading) return;
    
    setDownloading(photoId);
    setDownloadProgress(0);
    
    try {
      // Step 1: Get signed URL from backend
      const urlResponse = await axios.get(`${API}/gallery/download/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { download_url, file_name, file_size } = urlResponse.data;
      const actualFilename = filename || file_name || 'photo.jpg';
      
      toast.loading(
        <div className="text-sm">
          <div className="font-medium">Mengunduh: {actualFilename}</div>
          <div className="text-gray-500">Ukuran: {formatFileSize(file_size)}</div>
        </div>, 
        { id: 'download' }
      );
      
      // Step 2: Download file with progress tracking
      const response = await axios.get(download_url, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          const total = progressEvent.total || file_size;
          if (total) {
            const progress = Math.round((progressEvent.loaded / total) * 100);
            setDownloadProgress(progress);
          }
        }
      });
      
      // Step 3: Trigger browser download
      const blob = new Blob([response.data]);
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = actualFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      toast.success(
        <div className="text-sm">
          <div className="font-medium">Download selesai!</div>
          <div className="text-gray-500">{actualFilename}</div>
        </div>, 
        { id: 'download' }
      );
      
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.response?.data?.detail || 'Gagal mendownload foto', { id: 'download' });
    } finally {
      setDownloading(null);
      setDownloadProgress(0);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat galeri...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-ltb-br relative pb-20">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_bc6efd8f-02f7-4fdc-a397-f7bf259d1836/artifacts/uov20vz1_Pattern%20LTB.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}></div>
      
      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <header className="bg-white shadow-md py-3 sm:py-4 px-4 sm:px-6">
          <div className="max-w-6xl mx-auto flex justify-between items-center">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="font-medium">Kembali</span>
            </button>
            <h1 className="text-lg sm:text-xl font-bold text-gray-800">Galeri Foto</h1>
            <div className="flex items-center gap-2">
              <div className="bg-blue-500 text-white px-3 py-1.5 rounded-lg text-sm font-medium">
                {user?.tokens_balance || 0} Token
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-6xl mx-auto px-4 py-6">
          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-lg mb-6 p-1 flex">
            <button
              onClick={() => setActiveTab('gallery')}
              className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                activeTab === 'gallery'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Semua Foto ({photos.length})
              </span>
            </button>
            <button
              onClick={() => setActiveTab('collection')}
              className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                activeTab === 'collection'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                Koleksi Saya ({myPhotos.length})
              </span>
            </button>
          </div>

          {/* Gallery Tab */}
          {activeTab === 'gallery' && (
            <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
              {photos.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-20 h-20 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-500">Belum ada foto di galeri</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
                  {photos.map((photo) => (
                    <div
                      key={photo.id}
                      className="relative group cursor-pointer rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all"
                      onClick={() => setSelectedPhoto(photo)}
                    >
                      <img
                        src={photo.preview_url}
                        alt={photo.nama_file}
                        className="w-full aspect-square object-cover"
                        loading="lazy"
                      />
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-white font-semibold">Lihat</span>
                      </div>
                      
                      {/* Price/Status Badge */}
                      <div className="absolute top-2 right-2">
                        {photo.is_purchased ? (
                          <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                            ✓ Dimiliki
                          </span>
                        ) : (
                          <span className="bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                            {photo.harga} Token
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Collection Tab */}
          {activeTab === 'collection' && (
            <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
              {myPhotos.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-20 h-20 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <p className="text-gray-500 mb-4">Belum ada foto di koleksi Anda</p>
                  <button
                    onClick={() => setActiveTab('gallery')}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold"
                  >
                    Lihat Galeri
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
                  {myPhotos.map((photo) => (
                    <div
                      key={photo.purchase_id}
                      className="relative group cursor-pointer rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all"
                    >
                      <img
                        src={photo.preview_url}
                        alt={photo.nama_file}
                        className="w-full aspect-square object-cover"
                        loading="lazy"
                      />
                      
                      {/* Download Overlay */}
                      <div 
                        className={`absolute inset-0 bg-black/50 ${downloading === photo.photo_id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity flex items-center justify-center cursor-pointer`}
                        onClick={() => !downloading && handleDownload(photo.photo_id, photo.nama_file)}
                      >
                        {downloading === photo.photo_id ? (
                          <div className="text-center text-white p-4">
                            <div className="w-16 h-16 mx-auto mb-2 relative">
                              <svg className="w-16 h-16 transform -rotate-90">
                                <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="4" />
                                <circle 
                                  cx="32" cy="32" r="28" fill="none" stroke="white" strokeWidth="4"
                                  strokeDasharray={`${2 * Math.PI * 28}`}
                                  strokeDashoffset={`${2 * Math.PI * 28 * (1 - downloadProgress / 100)}`}
                                  strokeLinecap="round"
                                />
                              </svg>
                              <span className="absolute inset-0 flex items-center justify-center text-sm font-bold">
                                {downloadProgress}%
                              </span>
                            </div>
                            <span className="text-sm">Mengunduh...</span>
                          </div>
                        ) : (
                          <div className="text-center text-white">
                            <svg className="w-8 h-8 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            <span className="font-semibold text-sm">Download</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Owned Badge */}
                      <div className="absolute top-2 right-2">
                        <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                          ✓ Dimiliki
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-6">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-medium text-blue-800">Cara Membeli Foto</p>
                <ul className="text-sm text-blue-700 mt-1 list-disc list-inside">
                  <li>Klik foto untuk melihat preview</li>
                  <li>Tekan tombol "Beli" untuk membeli dengan token</li>
                  <li>Foto yang sudah dibeli bisa didownload di "Koleksi Saya"</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Photo Modal */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div 
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold text-lg truncate pr-4">{selectedPhoto.nama_file}</h3>
              <button
                onClick={() => setSelectedPhoto(null)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Image */}
            <div className="relative bg-gray-100">
              <img
                src={selectedPhoto.preview_url}
                alt={selectedPhoto.nama_file}
                className="w-full max-h-[60vh] object-contain"
              />
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t">
              {selectedPhoto.is_purchased ? (
                <button
                  onClick={() => !downloading && handleDownload(selectedPhoto.id, selectedPhoto.nama_file)}
                  disabled={downloading === selectedPhoto.id}
                  className="w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {downloading === selectedPhoto.id ? (
                    <>
                      <div className="relative w-6 h-6">
                        <svg className="w-6 h-6 transform -rotate-90">
                          <circle cx="12" cy="12" r="10" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
                          <circle 
                            cx="12" cy="12" r="10" fill="none" stroke="white" strokeWidth="2"
                            strokeDasharray={`${2 * Math.PI * 10}`}
                            strokeDashoffset={`${2 * Math.PI * 10 * (1 - downloadProgress / 100)}`}
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                      Mengunduh... {downloadProgress}%
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download Original (Tanpa Watermark)
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={() => handlePurchase(selectedPhoto)}
                  disabled={purchasing}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {purchasing ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Memproses...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      Beli dengan {selectedPhoto.harga} Token
                    </>
                  )}
                </button>
              )}
              
              {!selectedPhoto.is_purchased && (
                <p className="text-center text-sm text-gray-500 mt-2">
                  Saldo Anda: {user?.tokens_balance || 0} Token
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
